// src/ChatHeader.tsx
import React, { useState } from "react";
import { CalendarDays, PawPrint } from "lucide-react";

import ProfileSettingsModal from "./components/ProfileSettingsModal";
import CapybaraStudio from "./components/CapybaraStudio";
import AvatarCircle from "./components/ui/AvatarCircle";

import { VoiceKey } from "./config/voices";

interface ChatHeaderProps {
  onAccessCodeChange: (code: string) => void;

  // Opens the Practice Calendar
  onOpenPracticeCalendar: () => void;

  // Provided by ChatContainer after Init
  coins: number;
  unlockedItems: string[];
  equippedItems: string[];
  userId: string;

  // ✅ NEW: propagate Capybara Studio updates back to parent state
  onCoinsChange: (coins: number) => void;
  onUnlockedItemsChange: (items: string[]) => void;
  onEquippedItemsChange: (items: string[]) => void;

  // Optional nickname (therapist-controlled)
  nickname?: string;

  // Avatar ID (comes from backend or localStorage)
  avatarId?: string | null;

  // Setter from ChatContainer so avatar changes propagate upward
  onAvatarChange: (id: string) => void;

  // 🆕 Live voice update (passed through to ProfileSettingsModal)
  onVoiceKeyChange: (voiceKey: VoiceKey) => void;
}

export default function ChatHeader({
  onAccessCodeChange,
  onOpenPracticeCalendar,
  onVoiceKeyChange,
  coins,
  unlockedItems,
  equippedItems,
  userId,

  // ✅ NEW
  onCoinsChange,
  onUnlockedItemsChange,
  onEquippedItemsChange,

  nickname,
  avatarId,
  onAvatarChange,
}: ChatHeaderProps) {
  const [openSettings, setOpenSettings] = useState(false);
  const [openCapybara, setOpenCapybara] = useState(false);
  const calendarTooltipId = React.useId();
  const capybaraTooltipId = React.useId();
  const profileTooltipId = React.useId();

  const tooltipClassName =
    "pointer-events-none absolute top-full z-50 mt-1.5 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100";

  return (
    <div className="flex items-center justify-between p-3">
      {/* Left: Logo + title + nickname */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <img src="/favicon.png" alt="EFA logo" className="w-12 h-12" />

        <div className="flex flex-col leading-tight">
          <h1 className="whitespace-nowrap text-[17px] sm:text-xl font-semibold">
            {"OCD Homework Assistant"}
          </h1>

          {nickname && (
            <span className="text-sm text-gray-600">
              Your ERP homework companion
            </span>
          )}
        </div>
      </div>

      {/* Right: Practice Calendar + Capybara Studio + Avatar */}
      <div className="flex shrink-0 items-center gap-0">
        {/* Practice Calendar */}
        <div className="group relative inline-flex">
          <button
            type="button"
            onClick={onOpenPracticeCalendar}
            aria-label="Open Practice Calendar"
            aria-describedby={calendarTooltipId}
            className="inline-flex items-center justify-center h-[42px] w-[34px] sm:w-[38px] rounded-lg
        bg-white text-gray-800 hover:bg-gray-100 active:bg-gray-200
        focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
          >
            <CalendarDays className="w-5 h-5" />
          </button>
          <span
            id={calendarTooltipId}
            role="tooltip"
            className={`${tooltipClassName} left-1/2 -translate-x-1/2`}
          >
            Practice Calendar
          </span>
        </div>

        {/* Capybara Studio */}
        <div className="group relative inline-flex">
          <button
            type="button"
            onClick={() => setOpenCapybara(true)}
            aria-label="Dress the Capybara"
            aria-describedby={capybaraTooltipId}
            className="inline-flex items-center justify-center h-[42px] w-[34px] sm:w-[38px] rounded-lg
              bg-white text-gray-800 hover:bg-gray-100 active:bg-gray-200
              focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
          >
            <PawPrint className="w-5 h-5" />
          </button>
          <span
            id={capybaraTooltipId}
            role="tooltip"
            className={`${tooltipClassName} left-1/2 -translate-x-1/2`}
          >
            Dress the Capybara
          </span>
        </div>

        {/* Avatar Button → Opens Profile & Settings */}
        <div className="group relative ml-0.5 inline-flex">
          <AvatarCircle
            size={36}
            avatarId={avatarId || "avatar_default"}
            onClick={() => setOpenSettings(true)}
            ariaLabel="Profile & Settings"
            ariaDescribedBy={profileTooltipId}
          />
          <span
            id={profileTooltipId}
            role="tooltip"
            className={`${tooltipClassName} right-0`}
          >
            Profile &amp; Settings
          </span>
        </div>
      </div>

      {/* Profile & Settings Modal */}
      <ProfileSettingsModal
        open={openSettings}
        onClose={() => {
          setOpenSettings(false);

          // Re-sync access code with parent
          const code = localStorage.getItem("accessCode") || "";
          onAccessCodeChange(code);
        }}
        avatarId={avatarId ?? undefined}
        nickname={nickname}
        onAvatarChange={onAvatarChange}
        onVoiceKeyChange={onVoiceKeyChange}
      />

      {/* Capybara Studio Modal */}
      <CapybaraStudio
        open={openCapybara}
        onClose={() => setOpenCapybara(false)}
        coins={coins}
        unlockedItems={unlockedItems}
        equippedItems={equippedItems}
        userId={userId}
        onCoinsChange={onCoinsChange}
        onUnlockedItemsChange={onUnlockedItemsChange}
        onEquippedItemsChange={onEquippedItemsChange}
      />
    </div>
  );
}
